import { useMemo } from 'react';

import { useDebounce } from '@cast/utils';

import { UseBestPracticeChecksQuery } from 'hooks/queries/security-insights';

import { nonNamespacedLabel } from '../../constants';

type ChecksQueryFilters = Pick<
  UseBestPracticeChecksQuery,
  'standard' | 'clusterIds' | 'namespaces' | 'labels' | 'severities'
>;

export const useChecksQueryFilters = (
  values: Record<string, any>,
  scopedClusterIds?: string[]
): ChecksQueryFilters => {
  const {
    standard: selectedStandard,
    namespaces: selectedNamespaces,
    labels: selectedLabels,
    clusterIds: selectedClusters,
    severity: selectedSeverities,
  } = useDebounce(values);

  const clusterIds: string[] = useMemo(() => {
    if (scopedClusterIds?.length) {
      return scopedClusterIds;
    }
    if (selectedClusters?.length) {
      return selectedClusters;
    }
    return [];
  }, [scopedClusterIds, selectedClusters]);

  const namespaces: string[] = useMemo(() => {
    const _namespaces: string[] = [];
    if (selectedNamespaces?.length) {
      for (const ns of selectedNamespaces) {
        const normalizedNamespace = ns === nonNamespacedLabel ? '' : ns;
        if (!_namespaces.includes(normalizedNamespace)) {
          _namespaces.push(normalizedNamespace);
        }
      }
    }
    return _namespaces;
  }, [selectedNamespaces]);

  let standard = selectedStandard;
  let severities = selectedSeverities || [];
  if (selectedStandard === 'castBasics') {
    standard = 'cast';
    severities = [...new Set(['critical', 'high', ...severities])];
  }

  return {
    standard,
    clusterIds: clusterIds.length ? clusterIds : undefined,
    namespaces: namespaces.length ? namespaces : undefined,
    labels: selectedLabels?.length ? selectedLabels : undefined,
    severities,
  };
};
